import { Routes } from "@angular/router";
import { AppLayoutComponent } from "@app/layout/app-layout/app-layout.component";
import { authGuard, permissionGuard } from "@app/core/auth";
import { APP_ROUTES, PERMISSION } from "@app/constants";
import { LoginComponent } from "@app/modules/public/login/login.component";
import { LogoutComponent } from "@app/modules/public/logout/logout.component";
import { UnauthorizedComponent } from "@app/modules/public/unauthorized/unauthorized.component";
import { PageNotFoundComponent } from "@app/modules/public/page-not-found/page-not-found.component";
import { DashboardComponent } from "@app/modules/dashboard/dashboard.component";
import { ProfileComponent } from "@app/modules/admin/system-user/profile/profile.component";

export const routes: Routes = [
  {
    canActivate: [authGuard, permissionGuard],
    canActivateChild: [authGuard, permissionGuard],
    children: [
      {
        path: APP_ROUTES.DASHBOARD,
        component: DashboardComponent,
        data: {
          permissions: [PERMISSION.DASHBOARD_VIEW],
          breadcrumb: {
            label: "Dashboard",
          },
        },
      },
      {
        path: APP_ROUTES.USER_PROFILE,
        component: ProfileComponent,
        data: {
          permissions: [PERMISSION.DASHBOARD_VIEW],
          breadcrumb: {
            label: "Profile",
          },
        },
      },
      {
        path: `${APP_ROUTES.ADMIN}/${APP_ROUTES.ADMIN_USER}`,
        data: {
          permissions: [PERMISSION.USR_LIST],
          breadcrumb: {
            label: "Users",
          },
        },
        loadChildren: () =>
          import("./modules/admin/system-user/system-user.routes").then(
            (module) => module.systemUserRoutes
          ),
      },
      {
        path: `${APP_ROUTES.ADMIN}/${APP_ROUTES.ROLES}`,
        data: {
          permissions: [PERMISSION.ROLE_LIST],
          breadcrumb: {
            label: "Roles",
          },
        },
        loadChildren: () =>
          import("./modules/admin/system-role/system-role.routes").then(
            (module) => module.systemRoleRoutes
          ),
      },
      {
        path: `${APP_ROUTES.ADMIN}/${APP_ROUTES.PERMISSIONS}`,
        data: {
          permissions: [PERMISSION.PERMISSION_LIST],
          breadcrumb: {
            label: "Permissions",
          },
        },
        loadChildren: () =>
          import(
            "./modules/admin/system-permission/system-permission.routes"
          ).then((module) => module.systemPermissionRoutes),
      },

      //configurations/account-type
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.SETTINGS}`,
        data: {
          permissions: [PERMISSION.SETTING],
          breadcrumb: {
            label: "Settings",
          },
        },
        loadChildren: () =>
          import("./modules/configuration/settings/settings.routes").then(
            (module) => module.settingsRoutes
          ),
      },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.NOTICE}`,
        data: {
          permissions: [PERMISSION.NOTICE_LIST],
          breadcrumb: {
            label: "Notice",
          },
        },
        loadChildren: () =>
          import("./modules/configuration/notice/notice.routes").then(
            (module) => module.noticeRoutes
          ),
      },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.SMS}`,
        data: {
          permissions: [PERMISSION.DASHBOARD_VIEW], // Assuming general access for SMS view or shared permission
          breadcrumb: {
            label: "SMS",
          },
        },
        loadChildren: () =>
          import("./modules/configuration/sms/sms.routes").then(
            (module) => module.smsRoutes
          ),
      },
      // {
      //   path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.WELCOME_LETTER}`,
      //   data: {
      //     permissions: [PERMISSION.DASHBOARD_VIEW],
      //     breadcrumb: {
      //       label: 'Welcome Letter',
      //     },
      //   },
      //   loadChildren: () =>
      //     import(
      //       './modules/configuration/welcome-letter/welcome-letter.routes'
      //     ).then((module) => module.welcomeLetterRoutes),
      // },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.ACCOUNT_TYPE}`,
        data: {
          permissions: [PERMISSION.ACC_TYPE_LIST],
          breadcrumb: {
            label: "Account types",
          },
        },
        loadChildren: () =>
          import(
            "./modules/configuration/account-type/account-type.routes"
          ).then((module) => module.accountTypeRoutes),
      },
      //configurations/product-type
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.PRODUCT_TYPE}`,
        data: {
          permissions: [PERMISSION.PRODUCT_TYPE_LIST],
          breadcrumb: {
            label: "Product types",
          },
        },
        loadChildren: () =>
          import(
            "./modules/configuration/product-type/product-type.routes"
          ).then((module) => module.productTypeRoutes),
      },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.SERVICE_TYPE}`,
        data: {
          permissions: [PERMISSION.SERVICE_TYPE_LIST],
          breadcrumb: {
            label: "Service types",
          },
        },
        loadChildren: () =>
          import(
            "./modules/configuration/service-type/service-type.routes"
          ).then((module) => module.serviceTypeRoutes),
      },

      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.PAYMENT_OPTION}`,
        data: {
          permissions: [PERMISSION.PAYOPT_LIST],
          breadcrumb: {
            label: "Payment options",
          },
        },
        loadChildren: () =>
          import("./modules/payment-option/payment-option.route").then(
            (module) => module.paymentOptionRoutes
          ),
      },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.FEE_PROFILE}`,
        data: {
          permissions: [PERMISSION.FEEPR_LIST],
          breadcrumb: {
            label: "Fee Profile",
          },
        },
        loadChildren: () =>
          import("./modules/fee-profile/fee-profile.route").then(
            (module) => module.feeProfileRoutes
          ),
      },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.WAIVER}`,
        data: {
          permissions: [PERMISSION.WVR_LIST],
          breadcrumb: {
            label: "Waiver",
          },
        },
        loadChildren: () =>
          import("./modules/configuration/waiver/waiver.routes").then(
            (module) => module.waiverRoutes
          ),
      },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.MEMBER_BANK}`,
        data: {
          permissions: [PERMISSION.DASHBOARD_VIEW], // Placeholder for Member Bank
          breadcrumb: {
            label: "Member Bank",
          },
        },
        loadChildren: () =>
          import("./modules/configuration/member-bank/member-bank.routes").then(
            (module) => module.memberBankRoutes
          ),
      },

      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.BRANCH}`,
        data: {
          permissions: [PERMISSION.BRANCH_LIST],
          breadcrumb: {
            label: "Branch",
          },
        },
        loadChildren: () =>
          import("./modules/branch/branch.routes").then(
            (module) => module.branchRoutes
          ),
      },

      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.CHARGE}`,
        data: {
          permissions: [PERMISSION.CHARGE_PROFILE_LIST],
          breadcrumb: {
            label: "Charges",
          },
        },
        loadChildren: () =>
          import("./modules/charge/charge.route").then(
            (module) => module.chargeRoutes
          ),
      },

      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.TASK_SCHEDULER}`,
        data: {
          permissions: [PERMISSION.SCHEDULER_JOBS_LIST],
          breadcrumb: {
            label: "Scheduler Job List ",
          },
        },
        loadChildren: () =>
          import(
            "./modules/configuration/scheduler/scheduler-config.routes"
          ).then((module) => module.schedulerConfigRoutes),
      },

      {
        path: `${APP_ROUTES.CARD}`,
        data: {
          permissions: [
            PERMISSION.DEBIT_PENDING_LIST,
            PERMISSION.DEBIT_LIST_FOR_SEND_TO_SWITCH,
            PERMISSION.DEBIT_LIST_FOR_CARD_MAPPING,
            PERMISSION.DEBIT_LIST_FOR_CARD_DISPATCH,
            PERMISSION.DEBIT_LIST_FOR_PIN_DISPATCH,
            PERMISSION.DEBIT_LIST_FOR_CARD_ACK,
            PERMISSION.CARD_REPORT,
          ],
          breadcrumb: {
            label: "Card",
          },
        },
        loadChildren: () =>
          import("./modules/card/card.routes").then(
            (module) => module.cardRoutes
          ),
      },
      {
        path: `${APP_ROUTES.PIN}`,
        data: {
          permissions: [
            PERMISSION.DEBIT_LIST_FOR_PIN_ACK,
            PERMISSION.CREDIT_LIST_FOR_PIN_ACK,
            PERMISSION.PREPAID_LIST_FOR_PIN_ACK,
            PERMISSION.INSTANT_LIST_FOR_PIN_ACK,
          ],
          breadcrumb: {
            label: "Pin",
          },
        },
        loadChildren: () =>
          import("./modules/pin/pin.routes").then((module) => module.pinRoutes),
      },
      {
        path: `${APP_ROUTES.CREDIT_CARDS}`,
        data: {
          permissions: [
            PERMISSION.CREDIT_ALL_CARD_LIST,
            PERMISSION.CREDIT_DOC_VERIFICATION_LIST,
            PERMISSION.CREDIT_DOC_VERIFICATION_APPROVAL_LIST,
            PERMISSION.CREDIT_LIST_FOR_SEND_SWITCH,
            PERMISSION.CREDIT_LIST_FOR_MAPPING,
            PERMISSION.CREDIT_LIST_FOR_CARD_DISPATCH,
            PERMISSION.CREDIT_LIST_FOR_PIN_DISPATCH,
            PERMISSION.CREDIT_LIST_FOR_CARD_ACK,
            PERMISSION.CREDIT_LIST_FOR_PIN_ACK,
            PERMISSION.CREDIT_LIST_FOR_CARD_DELIVERY,
            PERMISSION.CREDIT_LIST_FOR_PIN_DELIVERY,
            PERMISSION.CREDIT_LIST_FOR_CARD_ACTIVATION,
          ],
          breadcrumb: {
            label: "Credit Card",
          },
        },
        loadChildren: () =>
          import("./modules/card/credit-cards/credit-card.routes").then(
            (module) => module.creditCardRoutes
          ),
      },
      // {
      //   path: `${APP_ROUTES.PIN}`,
      //   data: {
      //     //permissions: [PERMISSION.CARD_VIEW],
      //     breadcrumb: {
      //       label: 'Pin',
      //     },
      //   },
      //   loadChildren: () =>
      //     import('./modules/pin/pin.routes').then((module) => module.pinRoutes),
      // },
      {
        path: `${APP_ROUTES.PIN_REFRESH}`,
        data: {
          permissions: [PERMISSION.REPIN_LIST],
          breadcrumb: {
            label: "Pin Refresh",
          },
        },
        loadChildren: () =>
          import("./modules/pin/pin.routes").then(
            (module) => module.pinRefreshRoutes
          ),
      },
      {
        path: `${APP_ROUTES.PIN_STATUS_CHANGE}`,
        data: {
          // permissions: [PERMISSION.REPIN_UPDATE],
          permissions: [PERMISSION.REPIN_LIST],
          breadcrumb: {
            label: "Pin Status Change",
          },
        },
        loadChildren: () =>
          import("./modules/pin/pin.routes").then(
            (module) => module.pinStatusChangeRoutes
          ),
      },
      {
        path: `${APP_ROUTES.REPIN}`,
        data: {
          permissions: [PERMISSION.REPIN_LIST],
          breadcrumb: {
            label: "Pin",
          },
        },
        loadChildren: () =>
          import("./modules/pin/pin.routes").then(
            (module) => module.repinRoutes
          ),
      },
      {
        path: `${APP_ROUTES.CHARGE}`,
        data: {
          //permissions: [PERMISSION.CARD_VIEW],
          breadcrumb: {
            label: "Charges",
          },
        },
        loadChildren: () =>
          import("./modules/charge/charge.route").then(
            (module) => module.chargeRoutes
          ),
      },
      {
        path: `${APP_ROUTES.INSTANT}`,
        data: {
          permissions: [PERMISSION.INSTANT_ALL_CARD_LIST],
          breadcrumb: {
            label: "Instant Card",
          },
        },
        loadChildren: () =>
          import("./modules/card/card-instant/instant-card.route").then(
            (module) => module.instantCardTypeRoutes
          ),
      },
      {
        path: `${APP_ROUTES.PREPAID}`,
        data: {
          permissions: [PERMISSION.PREPAID_ALL_CARD_LIST],
          breadcrumb: {
            label: "Prepaid Card",
          },
        },
        loadChildren: () =>
          import("./modules/card/card-prepaid/prepaid-card.route").then(
            (module) => module.prepaidCardRoutes
          ),
      },
      {
        path: `${APP_ROUTES.REPORTS}`,
        data: {
          // permissions: [PERMISSION.CARD_REPORT],
          breadcrumb: {
            label: "Report Card",
          },
        },
        loadChildren: () =>
          import("./modules/report/report.route").then(
            (module) => module.ccsReportsRoutes
          ),
      },
      {
        path: `${APP_ROUTES.CARD_REPROCESS}`,
        data: {
          //permissions: [PERMISSION.CARD_VIEW],
          breadcrumb: {
            label: "Reprocess Cards",
          },
        },
        loadChildren: () =>
          import("./modules/reprocess/reprocess.route").then(
            (module) => module.ccsReprocessRoutes
          ),
      },
      {
        path: `${"api-request"}`,
        data: {
          permissions: [PERMISSION.DASHBOARD_VIEW], // Placeholder for API Request
          breadcrumb: {
            label: "Request API",
          },
        },
        loadChildren: () =>
          import("./modules/card/api-request/api-request.route").then(
            (module) => module.apiRequestRoutes
          ),
      },
      {
        path: `${"cards-order"}`,
        data: {
          permissions: [PERMISSION.CRDREQ_VIEW],
          breadcrumb: {
            label: "Card Order",
          },
        },
        loadChildren: () =>
          import("./modules/card-order/order.route").then(
            (module) => module.orderRoutes
          ),
      },

      {
        path: `${APP_ROUTES.CUSTOMERS}`,
        data: {
          //permissions: [PERMISSION.CARD_VIEW],
          breadcrumb: {
            label: "Customer Management",
          },
        },
        loadChildren: () =>
          // import("./modules/admin/system-user/system-user.routes").then(
          import("./modules/customer/customer.routes").then(
            (module) => module.customerRoutes
          ),
      },
      {
        path: `${APP_ROUTES.MIGRATE}`,
        data: {
          permissions: [PERMISSION.DATA_MGN01, PERMISSION.DATA_MGN02, PERMISSION.DATA_MGN03],
          breadcrumb: {
            label: "Data Migrate",
          },
        },
        loadChildren: () =>
          import("./modules/migrate/migrate.routes").then(
            (module) => module.migrateDataRoutes
          ),
      },
      {
        path: `${APP_ROUTES.DIRECT_CARD_BLOCK}`,
        data: {
          permissions: [PERMISSION.DIRECT_BLOCK_VIEW],
          breadcrumb: {
            label: "Direct Card Block",
          },
        },
        loadChildren: () =>
          import("./modules/direct-eft-card-block/direct-eft-card-block.routes").then(
            (module) => module.directEftCardBlockRoutes
          ),
      },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.NOTIFICATIONS}`,
        data: {
          permissions: [PERMISSION.NOTIFICATION_VIEW],
          breadcrumb: {
            label: "Notifications",
          },
        },
        loadChildren: () =>
          import(
            "./modules/configuration/notifications/notifications.routes"
          ).then((module) => module.notificationsRoutes),
      },
      {
        path: `${APP_ROUTES.CONFIGURATION}/${APP_ROUTES.LETTER}`,
        data: {
          // permissions: [PERMISSION.LETTER_LIST],
          breadcrumb: {
            label: "Letters",
          },
        },
        loadChildren: () =>
          import("./modules/configuration/letter/letter.routes").then(
            (module) => module.letterRoutes
          ),
      },
      {
        path: "",
        pathMatch: "full",
        redirectTo: APP_ROUTES.DASHBOARD,
      },
      {
        path: APP_ROUTES.UNAUTHORIZED,
        component: UnauthorizedComponent,
      },
    ],
    component: AppLayoutComponent,
    path: "",
  },
  {
    path: APP_ROUTES.LOGIN,
    component: LoginComponent,
  },
  {
    path: APP_ROUTES.LOGOUT,
    component: LogoutComponent,
  },

  // {
  //   path: APP_ROUTES.UNAUTHORIZED,
  //   component: UnauthorizedComponent,
  // },
  { path: APP_ROUTES.PAGE_NOT_FOUND, component: PageNotFoundComponent },
  { path: "**", redirectTo: APP_ROUTES.PAGE_NOT_FOUND },
  // { path: 'redirect', component: RedirectComponent }
];
